/**
 * The folder door — ADR-033 phase 7.
 *
 * World's ingest (`scripts/ingest-avatar-package.mjs`, World PR #1631) reads
 * ONE folder: `avatar.json` · `<id>.glb` (no animations; a stamped
 * `extras.poqpoq.package.id` must equal the record's) · `hero/<id>.png`.
 * Extra takes under `hero/` are ignored, never copied — kept for the author.
 *
 * Two halves:
 *  - `planPackageFolder` is PURE: the packaged avatar (phase 2's GLB with the
 *    record stamped in) + the takes → the list of files, byte for byte, with
 *    the starred take under the record's `thumbnail` name. Tested.
 *  - the writers are DOM: a directory handle taken at the GESTURE (the #140
 *    rule — before any export work), each file through `createWritable`;
 *    where the browser has no directory picker, the same plan leaves as one
 *    stored zip through the save picker (design note §4).
 *
 * Depends on: package-avatar (types), store-zip. Depended on by: main, tests.
 */
import type { PackageRecord } from '@harryhausen/kernel';
import { storeZip } from './store-zip.js';

export interface PackageFile {
  /** Forward-slash path inside the package folder. */
  readonly path: string;
  readonly bytes: Uint8Array;
  /** What the file is for — the status line and the smoke read this. */
  readonly role: 'record' | 'body' | 'hero' | 'take';
}

export interface PackagePlan {
  readonly id: string;
  readonly files: readonly PackageFile[];
  /** Take names the Document lists that this session has no bytes for — said, never silently skipped. */
  readonly missingTakes: readonly string[];
}

export interface PlanInputs {
  readonly record: PackageRecord;
  readonly glb: ArrayBuffer;
  /** Every take's PNG bytes this session holds, by the Document's take name. */
  readonly takes: ReadonlyMap<string, Uint8Array>;
  /** The Document's take list and star, in order. */
  readonly heroTakes: readonly string[];
  readonly heroFavourite: string;
}

export function planPackageFolder(inputs: PlanInputs): PackagePlan {
  const { record, heroFavourite } = inputs;
  const star = inputs.takes.get(heroFavourite);
  if (star === undefined) throw new Error(`the starred take "${heroFavourite}" has no PNG in this session — retake it`);
  if (record.thumbnail !== `${record.id}.png`) throw new Error(`the record names its card "${record.thumbnail}", not "${record.id}.png"`);
  const files: PackageFile[] = [
    { path: 'avatar.json', bytes: new TextEncoder().encode(`${JSON.stringify(record, null, 2)}\n`), role: 'record' },
    { path: `${record.id}.glb`, bytes: new Uint8Array(inputs.glb), role: 'body' },
    { path: `hero/${record.thumbnail}`, bytes: star, role: 'hero' },
  ];
  const missingTakes: string[] = [];
  for (const name of inputs.heroTakes) {
    if (name === heroFavourite) continue;
    const bytes = inputs.takes.get(name);
    if (bytes === undefined) missingTakes.push(name);
    else files.push({ path: `hero/${name}`, bytes, role: 'take' });
  }
  return { id: record.id, files, missingTakes };
}

/**
 * The order files are WRITTEN: the record last. A re-package over an existing
 * folder that fails on the body or a take would otherwise leave a NEW
 * `avatar.json` beside OLD or truncated assets — a folder that reads as the
 * new package and is not (Codex P2, #156). Body, hero, takes, then the record
 * as the completion marker.
 */
export function writeOrder(files: readonly PackageFile[]): PackageFile[] {
  const rank: Record<PackageFile['role'], number> = { body: 0, hero: 1, take: 2, record: 3 };
  return [...files].sort((a, b) => rank[a.role] - rank[b.role]);
}

/** One line for the status bar: what was written, and what was left out. */
export function packageLine(plan: PackagePlan, where: string): string {
  const body = plan.files.find((f) => f.role === 'body')!;
  const takes = plan.files.filter((f) => f.role === 'take').length;
  const mb = (body.bytes.length / 1_048_576).toFixed(1);
  return (
    `packaged ${plan.id} → ${where}: avatar.json · ${body.path} (${mb} MB, no animations) · hero/${plan.id}.png` +
    (takes > 0 ? ` · ${takes} other take(s) kept for you (ingest ignores them)` : '') +
    (plan.missingTakes.length > 0 ? ` · left out: ${plan.missingTakes.join(', ')} (no PNG in this session)` : '')
  );
}

// ───────────────────────────── the DOM half ─────────────────────────────

interface WritableHandle {
  createWritable(): Promise<{ write(data: Blob | Uint8Array): Promise<void>; close(): Promise<void> }>;
  getFile?(): Promise<{ size: number }>;
}
export interface DirectoryHandleLike {
  readonly name: string;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<DirectoryHandleLike>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<WritableHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
}
interface DirectoryPickerWindow {
  showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite'; id?: string }) => Promise<DirectoryHandleLike>;
  showSaveFilePicker?: (options: { suggestedName: string; types: { description: string; accept: Record<string, string[]> }[] }) => Promise<WritableHandle>;
}

export type PackageDestination =
  | { kind: 'folder'; dir: DirectoryHandleLike }
  | { kind: 'zip'; handle: WritableHandle; filename: string }
  | { kind: 'download'; filename: string }
  | { kind: 'cancelled' };

/**
 * WHERE, taken at the gesture: a writable directory where the browser has
 * one; else one zip through the save picker; else a download. Cancel is
 * cancel — never a folder the person did not choose.
 */
export async function pickPackageDestination(id: string): Promise<PackageDestination> {
  const w = window as DirectoryPickerWindow;
  const filename = `${id}.zip`;
  if (w.showDirectoryPicker !== undefined) {
    try {
      return { kind: 'folder', dir: await w.showDirectoryPicker({ mode: 'readwrite', id: 'harry-package' }) };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return { kind: 'cancelled' };
      throw err;
    }
  }
  if (w.showSaveFilePicker !== undefined) {
    try {
      return { kind: 'zip', handle: await w.showSaveFilePicker({ suggestedName: filename, types: [{ description: 'Zip archive', accept: { 'application/zip': ['.zip'] } }] }), filename };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return { kind: 'cancelled' };
      return { kind: 'download', filename };
    }
  }
  return { kind: 'download', filename };
}

async function writeFile(handle: WritableHandle, bytes: Uint8Array): Promise<void> {
  const w = await handle.createWritable();
  await w.write(bytes);
  await w.close();
  const landed = await handle.getFile?.();
  if (landed !== undefined && landed.size !== bytes.length) throw new Error(`the destination holds ${landed.size} bytes, not ${bytes.length} — the write did not take`);
}

/** Write the plan where it was asked to go. Returns where it landed, for the status line. */
export async function writePackage(plan: PackagePlan, dest: Exclude<PackageDestination, { kind: 'cancelled' }>): Promise<string> {
  if (dest.kind === 'folder') {
    // The package folder is `<id>/` INSIDE the chosen directory (design note §3), so a
    // person can point at a shelf of packages and each lands in its own folder.
    const root = await dest.dir.getDirectoryHandle(plan.id, { create: true });
    // A REPLACEMENT is visibly incomplete until it completes: the old record
    // goes first, before any asset is touched, and the new one lands last —
    // so a folder mid-write (or after a failed write) has no `avatar.json`
    // and World's ingest refuses it, never ingests a stale record over new
    // bytes or a new record over old ones. The API has no directory rename,
    // so this is the honest shape of atomicity here (Codex P2, #156 round two).
    try {
      await root.removeEntry('avatar.json');
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'NotFoundError')) throw err;
    }
    for (const f of writeOrder(plan.files)) {
      const segments = f.path.split('/');
      let dir = root;
      for (const seg of segments.slice(0, -1)) dir = await dir.getDirectoryHandle(seg, { create: true });
      await writeFile(await dir.getFileHandle(segments[segments.length - 1]!, { create: true }), f.bytes);
    }
    return `${dest.dir.name}/${plan.id}/`;
  }
  const zip = storeZip(writeOrder(plan.files).map((f) => ({ path: `${plan.id}/${f.path}`, bytes: f.bytes })));
  if (dest.kind === 'zip') {
    await writeFile(dest.handle, zip);
    return dest.filename;
  }
  const url = URL.createObjectURL(new Blob([zip.slice().buffer as ArrayBuffer], { type: 'application/zip' }));
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = dest.filename;
    a.click();
  } finally {
    URL.revokeObjectURL(url);
  }
  return `Downloads/${dest.filename}`;
}
